import { Image } from "../../image";
import { Box } from "../../../box";
import { useImagePreviewHandlers, useImagePreviewState } from "../../image-preview-context";
import styles from "../../image.module.scss";
import { ImagePreviewViewerProps } from "./viewer.types";
import classnames from "classnames";
import { useCallback, useMemo } from "react";

/**
 * The inner viewer component for the ImagePreview modal.
 * Responsible for rendering the currently selected image and handling pan/drag interactions.
 *
 * @param {ImagePreviewViewerProps} props - Component props.
 * @returns {JSX.Element | null} The image viewer container or null if no image is available.
 */
export const ImagePreviewViewer = ({ className }: ImagePreviewViewerProps) => {
    const {
        images,
        currentIndex,
        state: { scale, rotation, translate, isDragging },
    } = useImagePreviewState();
    const { close, setTranslate, setIsDragging, setLastMousePos, lastMousePos } = useImagePreviewHandlers();

    const currentImage = useMemo(() => images[currentIndex], [images, currentIndex]);

    const handleMouseDown = useCallback(
        (mouseEvent: React.MouseEvent) => {
            mouseEvent.stopPropagation();
            if (scale <= 1 && rotation === 0) return;
            setIsDragging(true);
            setLastMousePos({ x: mouseEvent.clientX, y: mouseEvent.clientY });
        },
        [scale, rotation, setIsDragging, setLastMousePos]
    );

    const handleMouseMove = useCallback(
        (mouseEvent: React.MouseEvent) => {
            if (!isDragging) return;
            const distanceX = mouseEvent.clientX - lastMousePos.x;
            const distanceY = mouseEvent.clientY - lastMousePos.y;
            setTranslate((translation) => ({ x: translation.x + distanceX, y: translation.y + distanceY }));
            setLastMousePos({ x: mouseEvent.clientX, y: mouseEvent.clientY });
        },
        [isDragging, lastMousePos, setTranslate, setLastMousePos]
    );

    const handleMouseUp = useCallback(() => setIsDragging(false), [setIsDragging]);

    const handleBackgroundMouseDown = useCallback(
        (mouseEvent: React.MouseEvent) => {
            if (mouseEvent.target === mouseEvent.currentTarget) {
                close();
            }
        },
        [close]
    );

    if (!currentImage) return null;

    return (
        <Box
            tabIndex={0}
            data-mantine-stop-propagation="true"
            className={classnames(styles.viewer, className)}
            onMouseDown={handleBackgroundMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
        >
            <Image
                src={currentImage.src}
                alt={currentImage.alt}
                data-movable={scale > 1 || rotation !== 0}
                data-dragging={isDragging}
                onMouseDown={handleMouseDown}
                onDragStart={(e: React.DragEvent) => e.preventDefault()}
                draggable={false}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                style={{
                    transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale}) rotate(${rotation}deg)`,
                }}
            />
        </Box>
    );
};
